import { useSession } from "@clerk/clerk-react";
import { Tooltip } from "@heroui/react";
import {
  AlertInfo,
  ArrowUpRight,
  Euro,
  Link as URL,
  Repeat,
} from "assets/icons";
import ButtonNew from "components/buttonNew/buttonNew";
import AcquisitionDocumentsList from "components/documents/acquisitionDocumentsList";
import {
  EditableSelectComponent,
  EditableTextareaComponent,
  renderSection,
} from "components/index";
import { API_URL } from "constants/index";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import BuildingsTable from "screens/buildings/components/statusTab/buildingsTable";
import GoogleMapComponent from "screens/buildings/components/statusTab/googleMap";
import TagSelector from "screens/contacts/components/tagSelector";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { useUsers } from "shared/contexts/users.context";
import { ImagesContainer } from "shared/components/imagesContainer/imagesContainer";
import { ResourceSearchSelect, CreateContactModal, ResourceDisplay, ResourceValue } from "shared/components/ResourceSearchSelect";
import {
  Acquisition,
  AcquisitionConLabels,
  AcquisitionProLabels,
  AcquisitionSource,
  ApprovalStatus,
  CompleteStatus,
  DealType,
} from "shared/types/acquisition";
import { BuildingDTO } from "shared/types/building";
import { FileLabel } from "shared/types/file";
import { ResourceType } from "shared/types/resource";
import { applyMask } from "shared/utils/masks";
import { createPatchObject } from "shared/utils/patch";
import { AcquisitionEmailsList } from "components/acquisitionEmails/acquisitionEmailsList";
import { Spinner } from "@heroui/react";
import { ArrowLeft, Clipboard, PencilSimple } from "@phosphor-icons/react";
import AddAcquisition from "components/addAcquisition/addAcquisition";
import { AcquisitionPhase } from "shared/types/acquisition";

const AcquisitionDetails = () => {
  const { acquisitionId } = useParams<{ acquisitionId?: string }>();
  const navigate = useNavigate();
  const { users } = useUsers();
  // First option is a clear-to-null sentinel so users can remove an assignment.
  const userOptions = [
    { value: '', label: '— Niet toegewezen —' },
    ...users.map((u: any) => ({
      value: u.id,
      label:
        `${u.first_name || ''} ${u.last_name || ''}`.trim() ||
        u.email ||
        u.id,
    })),
  ];
  const userLabel = (id: string | undefined | null): string => {
    if (!id) return 'Niet toegewezen';
    const u = users.find((x: any) => x.id === id);
    if (!u) return id;
    return (
      `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || id
    );
  };
  const goBackToAcquisitions = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/acquisitions');
    }
  };
  const [acquisition, setAcquisition] = useState<Acquisition>();
  const [editedAcquisition, setEditedAcquisition] = useState<Acquisition>();
  const { session } = useSession();
  const [editMode, setEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedProLabels, setSelectedProLabels] = useState<string[]>(
    acquisition?.acquisitionProReasons || [],
  );
  const [selectedConLabels, setSelectedConLabels] = useState<string[]>(
    acquisition?.acquisitionConReasons || [],
  );
  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);

  // Modal state for CreateContactModal (lifted to parent for stability)
  const [createContactModalOpen, setCreateContactModalOpen] = useState(false);
  const [createContactInitialName, setCreateContactInitialName] = useState('');
  const [createContactTargetField, setCreateContactTargetField] = useState<'intermediary' | 'owner' | null>(null);

  // Check if acquisition needs completion (email import with no buildings)
  const needsCompletion = acquisition &&
    acquisition.isEmailImport &&
    (!acquisition.buildings || acquisition.buildings.length === 0);
  const fetchAcquisition = async () => {
    setIsLoading(true);
    try {
      let res = await fetchWithAuth(
        `${API_URL}/acquisitions/${acquisitionId}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        },
        session,
      );
      const acquisition = await res.json();
      setAcquisition(acquisition);
      setEditedAcquisition(acquisition);
      setSelectedProLabels(acquisition.acquisitionProReasons || []);
      setSelectedConLabels(acquisition.acquisitionConReasons || []);
    } catch (e) {
      console.error(e);
      toast.error("Er is een fout opgetreden bij het ophalen van acquisities.");
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    fetchAcquisition();
  }, [acquisitionId]);
  useEffect(() => {
    // Listener for Escape key to exit edit mode
    const handleKeydown = (e: KeyboardEvent) => {
      if (editMode && e.key === "Escape") {
        toggleEditMode();
      }
    };

    document.addEventListener("keydown", handleKeydown);
    return () => {
      // Cleanup event listener
      document.removeEventListener("keydown", handleKeydown);
    };
  }, [acquisition, editMode]);
  const handleProTagClick = (tag: string) => {
    if (selectedProLabels.includes(tag)) {
      // Remove tag
      setSelectedProLabels((prevTags) => prevTags.filter((t) => t !== tag));
    } else {
      // Add tag
      setSelectedProLabels((prevTags) => [...prevTags, tag]);
    }
  };
  const handleConTagClick = (tag: string) => {
    if (selectedProLabels.includes(tag)) {
      // Remove tag
      setSelectedConLabels((prevTags) => prevTags.filter((t) => t !== tag));
    } else {
      // Add tag
      setSelectedConLabels((prevTags) => [...prevTags, tag]);
    }
  };


  const saveAcquisitionUpdates = async () => {
    if (!acquisition || !editedAcquisition) return;

    const patchObject = createPatchObject(acquisition, editedAcquisition);
    patchObject.acquisitionProReasons = selectedProLabels;
    patchObject.acquisitionConReasons = selectedConLabels;


    // Handle special cases for intermediary and owner
    if (editedAcquisition.intermediary && editedAcquisition.intermediary.id !== (acquisition as any).intermediaryResourceId) {
      patchObject.intermediaryResourceId = editedAcquisition.intermediary.id;
    }
    if (editedAcquisition.owner && editedAcquisition.owner.id !== (acquisition as any).ownerResourceId) {
      patchObject.ownerResourceId = editedAcquisition.owner.id;
    }

    if (Object.keys(patchObject).length === 0) {
      toast("No changes detected!");
      return;
    }

    try {
      const response = await fetchWithAuth(
        `${API_URL}/acquisitions/${acquisition.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(patchObject),
        },
        session
      );

      if (!response.ok) {
        throw new Error('Failed to save acquisition');
      }

      const updatedAcquisition = await response.json();
      setAcquisition(updatedAcquisition);
      setEditedAcquisition(updatedAcquisition);
      setEditMode(false);
      toast("Acquisitie opgeslagen!");
    } catch (error) {
      console.error("Failed to save acquisition: ", error);
      toast.error("Error saving acquisition!");
    }
  };

  // Auto-save when contact is created for intermediary or owner
  const saveContactToAcquisition = async (field: 'intermediary' | 'owner', contact: ResourceValue) => {
    if (!acquisition) return;

    const fieldKey = field === 'intermediary' ? 'intermediaryResourceId' : 'ownerResourceId';

    try {
      const response = await fetchWithAuth(
        `${API_URL}/acquisitions/${acquisition.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ [fieldKey]: contact.id }),
        },
        session
      );

      if (!response.ok) {
        throw new Error(`Failed to save ${field}`);
      }

      const updatedAcquisition = await response.json();
      setAcquisition(updatedAcquisition);
      setEditedAcquisition(updatedAcquisition);
      toast.success(`${field === 'intermediary' ? 'Tussenpersoon' : 'Eigenaar'} opgeslagen!`);
    } catch (error) {
      console.error(`Failed to save ${field}: `, error);
      toast.error(`Fout bij opslaan ${field === 'intermediary' ? 'tussenpersoon' : 'eigenaar'}`);
    }
  };

  const toggleEditMode = () => {
    setEditMode((prevMode) => !prevMode);
    if (!editMode) {
      setEditedAcquisition(acquisition); // initialize editedContact when entering edit mode
    }
  };
  const sharedAcquisitionProps = {
    editMode: editMode,
    isLoading: isLoading,
    onEditClick: toggleEditMode,
    onSaveClick: saveAcquisitionUpdates,
    editedState: editedAcquisition,
    originalState: acquisition,
    setStateFunction: setEditedAcquisition,
  };
  // const statusFieldsEvalProperty = [
  //   // acquisition.surfaceNonResidential = excelData['surfaceNonResidential'];
  //   // acquisition.valueParkings = excelData['valueParkings'];
  //   // acquisition.valueStorages = excelData['valueStorages'];
  //   // acquisition.valueBicycleStorages = excelData['valueBicycleStorages'];
  //   // acquisition.valueNonResidential = excelData['valueNonResidential'];
  //   // acquisition.valueResidential = excelData['valueResidential'];
  //   // acquisition.askingPriceSqmResidential = excelData['askingPriceSqmResidential'];
  //   // acquisition.bidPriceSqmResidential = excelData['bidPriceSqmResidential'];

  //   // acquisition.potentialGrossIncomeActual = excelData['potentialGrossIncomeActual'];
  //   // acquisition.netOperatingIncomeActual = excelData['netOperatingIncomeActual'];
  //   // acquisition.potentialGrossIncomeWorstCase = excelData['potentialGrossIncomeWorstCase'];
  //   // acquisition.netOperatingIncomeWorstCase = excelData['netOperatingIncomeWorstCase'];
  //   // acquisition.potentialGrossIncomeBestCase = excelData['potentialGrossIncomeBestCase'];
  //   // acquisition.netOperatingIncomeBestCase = excelData['netOperatingIncomeBestCase'];

  //   {
  //     label: "Vraagprijs (excl. kosten) (B9)",
  //     key: "grossAskingPrice",
  //     mask: "valuta",
  //   },
  //   {
  //     label: "Vraagprijs (incl. kosten) (C13)",
  //     key: "netAskingPrice",
  //     mask: "valuta",
  //   },
  //   {
  //     label: "Transactiekosten (B31)",
  //     key: "transactionCosts",
  //     mask: "valuta",
  //   },
  //   { label: "Renovatiebudget (B32)", key: "renovationBudget", mask: "valuta" },
  //   {
  //     label:
  //       "Gemiddelde prijs/unit residentieel incl. kosten en renovatie (D40)",
  //     key: "avgPriceResidentialUnit",
  //     mask: "valuta",
  //   },
  //   {
  //     label: "Geschatte yield kantoren/magazijn (B15)",
  //     key: "estimatedYieldOfficeStorage",
  //     mask: "percentage_comma",
  //   },
  //   {
  //     label: "Geschatte yield retail (B16)",
  //     key: "estimatedYieldRetail",
  //     mask: "percentage_comma",
  //   },
  //   { label: "# residentiële units (E9)", key: "totalResidentialUnits" },
  //   {
  //     label: "oppervlakte residentieel (E17)",
  //     key: "surfaceResidential",
  //     mask: "surface",
  //   },
  //   {
  //     label: "# niet-residentiële units (E10)",
  //     key: "totalNonResidentialUnits",
  //   },
  //   {
  //     label: "oppervlakte niet-residentieel (E16)",
  //     key: "surfaceNonResidential",
  //     mask: "surface",
  //   },
  //   { label: "# parkings overdekt (E11)", key: "totalParkingsWithCover" },
  //   { label: "# parkings niet-overdekt (E12)", key: "totalParkingsNoCover" },
  //   { label: "# bergingen (E13)", key: "totalStorages" },
  //   { label: "# fietsenbergingen (E14)", key: "bicycleStorages" },
  //   {
  //     label: "Prijs PP overdekt (B17)",
  //     key: "priceParkingPlacesCovered",
  //     mask: "valuta",
  //   },
  //   {
  //     label: "Prijs PP niet overdekt (B18)",
  //     key: "priceParkingPlacesNotCovered",
  //     mask: "valuta",
  //   },
  //   { label: "Prijs berging (B19)", key: "priceStorage", mask: "valuta" },
  //   {
  //     label: "Prijs fietsenberging (B20)",
  //     key: "priceBicycleStorage",
  //     mask: "valuta",
  //   },
  //   {
  //     label: "Geschatte marktwaarde (uitponding t=0)",
  //     key: "estimatedMarketValue",
  //     mask: "valuta",
  //   },
  //   {
  //     label: "Fiscale korting op het vastgoed (G19)",
  //     key: "fiscalDiscountRealEstate",
  //     mask: "valuta",
  //   },
  //   {
  //     label: "Fiscale korting op de vennootschap (G20)",
  //     key: "fiscalDiscountCompanyShares",
  //     mask: "valuta",
  //   },
  // ];
  // const yallyBidFields = [
  //   { label: "Biedprijs actueel (B28)", key: "bidPriceActual", mask: "valuta" },
  //   {
  //     label: "Biedprijs worst case (B29)",
  //     key: "bidPriceWorstCase",
  //     mask: "valuta",
  //   },
  //   {
  //     label: "Biedprijs best case (B30)",
  //     key: "bidPriceBestCase",
  //     mask: "valuta",
  //   },
  //   {
  //     label: "Totale cash-out (best case bod) (B33)",
  //     key: "totalCashOut",
  //     mask: "valuta",
  //   },

  //   {
  //     label: "Bod residentieel gedeelte (G13)",
  //     key: "valueResidential",
  //     mask: "valuta",
  //   },
  //   {
  //     label: "Bod niet-residentieel gedeelte (G14-G13)",
  //     key: "valueNonResidential",
  //     mask: "valuta",
  //   },
  //   {
  //     label: "Bod per m2 residentieel incl. kosten en renovatie (D42)",
  //     key: "bidPriceSqmResidential",
  //     mask: "valuta",
  //   },
  // ];
  // These fields are pulled from the connected Excel spreadsheet - not editable
  const statusFieldsEvalRent = [
    {
      label: "Bruto huur/jaar actueel (C76)",
      key: "potentialGrossIncomeActual",
      mask: "valuta",
      disableEditMode: true,
    },
    {
      label: "Bruto huur/jaar worst case",
      key: "potentialGrossIncomeWorstCase",
      mask: "valuta",
      disableEditMode: true,
    },
    {
      label: "Bruto huur/jaar best case",
      key: "potentialGrossIncomeBestCase",
      mask: "valuta",
      disableEditMode: true,
    },
    {
      label: "Net operating income actueel",
      key: "netOperatingIncomeActual",
      mask: "valuta",
      disableEditMode: true,
    },
    {
      label: "Net operating income worst case",
      key: "netOperatingIncomeWorstCase",
      mask: "valuta",
      disableEditMode: true,
    },
    {
      label: "Net operating income best case",
      key: "netOperatingIncomeBestCase",
      mask: "valuta",
      disableEditMode: true,
    },
  ];
  const statusFieldsGeneral = [
    {
      label: "Verantwoordelijke",
      key: "yallyResponsibleUserId",
      Component: () =>
        editMode ? (
          <EditableSelectComponent
            label="Verantwoordelijke"
            editMode={editMode}
            value={editedAcquisition?.yallyResponsibleUserId || ""}
            options={userOptions}
            onChange={(value: string) =>
              setEditedAcquisition((prev: any) => ({
                ...prev,
                yallyResponsibleUserId: value || null,
              }))
            }
          />
        ) : (
          <p className="font-medium">
            {userLabel(acquisition?.yallyResponsibleUserId)}
          </p>
        ),
    },
    {
      label: "Aangebracht door",
      key: "aangebrachtDoor",
      Component: () =>
        editMode ? (
          <EditableSelectComponent
            label="Aangebracht door"
            editMode={editMode}
            value={editedAcquisition?.aangebrachtDoor || ""}
            options={userOptions}
            onChange={(value: string) =>
              setEditedAcquisition((prev: any) => ({
                ...prev,
                aangebrachtDoor: value || null,
              }))
            }
          />
        ) : (
          <p className="font-medium">
            {userLabel(
              acquisition?.aangebrachtDoor ||
                acquisition?.yallyResponsibleUserId,
            )}
          </p>
        ),
    },
    {
      label: "Soort transactie",
      key: "dealType",
      Component: () => (
        <EditableSelectComponent
          label="Soort transactie?"
          editMode={editMode}
          value={editedAcquisition?.dealType || ""}
          options={Object.values(DealType).map((value: any) => ({
            value: value,
            label: value,
          }))}
          onChange={(value: string) =>
            setEditedAcquisition((prev: any) => ({
              ...prev,
              dealType: value,
            }))
          }
        />
      ),
    },
    {
      label: "Waarom wel voor dit dossier kiezen",
      key: "acquisitionProReasons",
      Component: () => {
        if (editMode) {
          return (
            <div className="flex flex-col gap-2">
              <p className="text-sm">Waarom wel voor dit dossier kiezen</p>
              <TagSelector
                tags={AcquisitionProLabels}
                selectedTags={selectedProLabels}
                handleTagClick={handleProTagClick}
              />
            </div>
          );
        } else if (
          acquisition?.acquisitionProReasons &&
          acquisition.acquisitionProReasons.length > 0
        ) {
          return (
            <div className="flex flex-wrap justify-end gap-2">
              {acquisition.acquisitionProReasons.sort().map((tag, index) => (
                <span
                  key={index}
                  className="h-fit min-w-fit rounded-full border border-moss-primary bg-white px-2 font-medium text-moss-primary"
                >
                  {tag}
                </span>
              ))}
            </div>
          );
        } else {
          return <p className="font-medium">Niet ingevuld</p>;
        }
      },
    },
    {
      label: "Waarom niet voor dit dossier kiezen",
      key: "acquisitionConReasons",
      Component: () => {
        if (editMode) {
          return (
            <div className="flex flex-col gap-2">
              <p className="text-sm">Waarom niet voor dit dossier kiezen</p>
              <TagSelector
                tags={AcquisitionConLabels}
                selectedTags={selectedConLabels}
                handleTagClick={handleConTagClick}
              />
            </div>
          );
        } else if (
          acquisition?.acquisitionConReasons &&
          acquisition.acquisitionConReasons.length > 0
        ) {
          return (
            <div className="flex flex-wrap justify-end gap-2">
              {acquisition.acquisitionConReasons.sort().map((tag, index) => (
                <span
                  key={index}
                  className="h-fit min-w-fit rounded-full border border-moss-primary bg-white px-2 font-medium text-moss-primary"
                >
                  {tag}
                </span>
              ))}
            </div>
          );
        } else {
          return <p className="font-medium">Niet ingevuld</p>;
        }
      },
    },
    { label: "Reden van verkoop", key: "saleReason" },
    {
      label: "ACQ bron",
      key: "acquisitionSource",
      Component: () => (
        <EditableSelectComponent
          label="ACQ bron"
          editMode={editMode}
          value={editedAcquisition?.acquisitionSource || ""}
          options={Object.values(AcquisitionSource).map((value: string) => ({
            value: value,
            label: value,
          }))}
          onChange={(value: string) =>
            setEditedAcquisition((prev: any) => ({
              ...prev,
              acquisitionSource: value || null,
            }))
          }
        />
      ),
    },
    {
      label: "Tussenpersoon",
      key: "intermediary",
      Component: () => {
        if (editMode) {
          return (
            <ResourceSearchSelect
              value={editedAcquisition && 'intermediary' in editedAcquisition
                ? (editedAcquisition.intermediary ?? null)
                : (acquisition?.intermediary ?? null)}
              onChange={(value) =>
                setEditedAcquisition((prev: any) => ({
                  ...prev,
                  intermediary: value,
                }))
              }
              resourceFilter={[ResourceType.COMPANY, ResourceType.CONTACT]}
              placeholder="Zoek tussenpersoon..."
              onCreateContact={(searchTerm) => {
                setCreateContactInitialName(searchTerm);
                setCreateContactTargetField('intermediary');
                setCreateContactModalOpen(true);
              }}
              isCreateModalOpen={createContactModalOpen && createContactTargetField === 'intermediary'}
            />
          );
        } else {
          return <ResourceDisplay value={acquisition?.intermediary || null} />;
        }
      },
    },
    {
      label: "Verkoper (= eigenaar op moment acquisitie)",
      key: "owner",
      Component: () => {
        if (editMode) {
          return (
            <ResourceSearchSelect
              value={editedAcquisition && 'owner' in editedAcquisition
                ? (editedAcquisition.owner ?? null)
                : (acquisition?.owner ?? null)}
              onChange={(value) =>
                setEditedAcquisition((prev: any) => ({ ...prev, owner: value }))
              }
              resourceFilter={[ResourceType.COMPANY, ResourceType.CONTACT]}
              placeholder="Zoek verkoper..."
              onCreateContact={(searchTerm) => {
                setCreateContactInitialName(searchTerm);
                setCreateContactTargetField('owner');
                setCreateContactModalOpen(true);
              }}
              isCreateModalOpen={createContactModalOpen && createContactTargetField === 'owner'}
            />
          );
        } else {
          return <ResourceDisplay value={acquisition?.owner || null} />;
        }
      },
    },
    {
      label: "ACQ bron URL",
      key: "acquisitionSourceUrl",
      Component: () => {
        return (
          <a
            className="flex items-center gap-1 font-medium text-moss-primary"
            target="_blank"
            href={acquisition?.acquisitionSourceUrl}
          >
            <URL className="h-[18px] w-[18px]" />
            Bezoek link
          </a>
        );
      },
    },
  ];
  const approvalStatusOptions = Object.values(ApprovalStatus).map((value) => ({
    value: value,
    label: value,
  }));
  const completeStatusOptions = Object.values(CompleteStatus).map((value) => ({
    value: value,
    label: value,
  }));
  const getApprovalStatusColor = (status: ApprovalStatus) => {
    console.log(status);
    switch (status) {
      case ApprovalStatus.APPROVED:
        return "bg-green-500";
      case ApprovalStatus.REJECTED:
        return "bg-red-500";
      default:
        return "bg-transparent";
    }
  };
  const getCompletionStatusColor = (status: CompleteStatus) => {
    switch (status) {
      case CompleteStatus.COMPLETE:
        return "bg-green-500";
      case CompleteStatus.INCOMPLETE:
        return "bg-red-500";
      default:
        return "bg-transparent";
    }
  };
  const appreciationFieldsGeneral = [
    {
      label: "Input van de interne verantwoordelijke",
      key: "acquisitionDealAppreciation",
      Component: EditableTextareaComponent,
    },
    {
      label: "Goedgekeurd door Acquisitiemeeting",
      key: "approvedAcq",
      Component: () =>
        editMode ? (
          <EditableSelectComponent
            label="Goedgekeurd door de Acquisitiemeeting?"
            editMode={editMode}
            value={
              editedAcquisition?.approvedAcq || acquisition?.approvedAcq || ""
            }
            options={approvalStatusOptions}
            onChange={(value: string) =>
              setEditedAcquisition((prev: any) => ({
                ...prev,
                approvedAcq: value,
              }))
            }
          />
        ) : (
          <span
            className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getApprovalStatusColor(
              editedAcquisition?.approvedAcq as any,
            )}`}
          />
        ),
    },
    {
      label: "Input van de Acquisitiemeeting",
      key: "acquisitionMeetingDealAppreciation",
      Component: EditableTextareaComponent,
    },
    {
      label: "Dossier volledig aangevuld voor Raad van Bestuur?",
      key: "infoComplete",
      Component: () =>
        editMode ? (
          <EditableSelectComponent
            label="Dossier volledig aangevuld voor Raad van Bestuur?"
            editMode={editMode}
            value={
              editedAcquisition?.infoComplete || acquisition?.infoComplete || ""
            }
            options={completeStatusOptions}
            onChange={(value: string) =>
              setEditedAcquisition((prev: any) => ({
                ...prev,
                infoComplete: value,
              }))
            }
          />
        ) : (
          <span
            className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getCompletionStatusColor(
              (editedAcquisition?.infoComplete as any) ||
              (acquisition?.approvedRvb as any),
            )}`}
          />
        ),
    },
    {
      label: "Goedgekeurd door Raad van Bestuur",
      key: "approvedRvb",
      Component: () =>
        editMode ? (
          <EditableSelectComponent
            label="Goedgekeurd door Raad van Bestuur?"
            editMode={editMode}
            value={
              editedAcquisition?.approvedRvb || acquisition?.approvedRvb || ""
            }
            options={approvalStatusOptions}
            onChange={(value: string) =>
              setEditedAcquisition((prev: any) => ({
                ...prev,
                approvedRvb: value,
              }))
            }
          />
        ) : (
          <span
            className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getApprovalStatusColor(
              (editedAcquisition?.approvedRvb as any) ||
              (acquisition?.approvedRvb as any),
            )}`}
          />
        ),
    },
    {
      label: "Input van de RvB",
      key: "rvbDealAppreciation",
      Component: EditableTextareaComponent,
    },
  ];
  interface CellTooltipProps {
    content: string;
  }
  const CellTooltip: React.FC<CellTooltipProps> = ({ content }) => {
    return (
      <Tooltip content={content}>
        <a href="#" onClick={(e) => e.preventDefault()}>
          <AlertInfo className="h-3.5 w-3.5 cursor-pointer text-neutral-secondary-mid" />
        </a>
      </Tooltip>
    );
  };

  const copyAcquisitionCode = () => {
    const code = `#ACQ-${acquisitionId}`;
    navigator.clipboard.writeText(code)
        .then(() => {
            toast.success(`Code ${code} gekopieerd naar klembord!`, {
                position: "bottom-right",
                autoClose: 2000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        })
        .catch(() => {
            toast.error('Kon code niet kopiëren naar klembord');
        });
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <Spinner size="lg" color="primary" />
        <p className="text-neutral-secondary-mid">Acquisitiedossier laden...</p>
      </div>
    );
  }

  if (!acquisition) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <p className="text-neutral-secondary-mid">Geen acquisitiedossier gevonden</p>
      </div>
    );
  }

  return (
    <div className="z-10 mb-10 flex flex-col gap-5 rounded-lg bg-white sm:bg-white sm:p-5 sm:shadow-md">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={goBackToAcquisitions}
            className="flex items-center gap-1 rounded-full border border-neutral-primary/20 px-3 py-1.5 text-sm font-medium text-neutral-secondary-mid hover:bg-neutral-100 hover:text-neutral-primary transition-colors"
            aria-label="Terug naar acquisities"
          >
            <ArrowLeft weight="bold" className="h-4 w-4" />
            Terug
          </button>
          <p className="text-sm font-medium uppercase text-neutral-secondary-mid">
            <span className="cursor-pointer hover:text-neutral-primary transition-colors" onClick={copyAcquisitionCode}>
              #ACQ-{acquisition?.id}
            </span>
            {" - "}
            {acquisition?.name}
          </p>
          <Tooltip
            content={`Druk hier om de e-mail code #ACQ-${acquisitionId} voor dit dossier te kopiëren. Plak deze daarna in het onderwerp van een nieuwe e-mail naar acquisitions@yally.be om deze hieraan toe te voegen`}
            placement="right"
            showArrow
            classNames={{
              base: "before:bg-default-200",
            }}
          >
            <Clipboard
              className="w-4 h-4 cursor-pointer text-neutral-secondary-mid hover:text-neutral-primary transition-colors" 
              onClick={copyAcquisitionCode}
            />
          </Tooltip>
        </div>
        {needsCompletion ? (
          <ButtonNew
            variant="primaryMoss"
            icon={<PencilSimple weight="duotone" />}
            label="Vervolledig dossier"
            onClick={() => setIsCompleteModalOpen(true)}
          />
        ) : (
          <a
            href={acquisition?.sharePointSpreadsheetUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <div className="flex items-center gap-2">
              <p className="text-sm">
                Excel laatst opgehaald op: {acquisition?.lastUpdatedExcel}
              </p>
              <ButtonNew
                variant="primaryMoss"
                icon={<ArrowUpRight />}
                label="Open Excel"
              />
            </div>
          </a>
        )}
      </header>
      <section className="grid grid-cols-1 gap-8 gap-y-12 sm:grid-cols-2">
        <div className="sm:col-span-1">
          <GoogleMapComponent
            googlePlaceLongAddress={
              (acquisition?.buildings &&
                acquisition.buildings.length > 0 &&
                acquisition.buildings[0].googlePlaceLongAddress) ||
              ""
            }
          />
        </div>
        <div className="sm:col-span-1">
          <ImagesContainer
            variant="light"
            fileLabels={[FileLabel.AcquisitionImages, FileLabel.CoverImage]}
            buildingId={
              (acquisition &&
                acquisition.buildings &&
                acquisition.buildings.length > 0 &&
                acquisition.buildings.length < 2 &&
                acquisition.buildings[0].id) ||
              undefined
            }
            buildings={
              (acquisition &&
                acquisition.buildings &&
                acquisition.buildings.length > 1 &&
                acquisition.buildings.map(
                  (building: BuildingDTO) => (building as any).id,
                )) ||
              []
            }
            customData={{ acquisitionId: acquisition?.id }}
          />
        </div>
        <div className="col-span-full">
          <BuildingsTable
            data={(acquisition && acquisition.buildings) || []}
            fetchAcquisition={fetchAcquisition}
            onCompleteAcquisition={needsCompletion ? () => setIsCompleteModalOpen(true) : undefined}
          />
        </div>
        <section className="col-span-full flex flex-col gap-8 text-sm">
          <header className="col-span-full flex items-center justify-between">
            <p className="flex items-center gap-1 text-lg">
              <Euro className="h-[18px] w-[18px]" />
              Financiële gegevens
            </p>
            <div className="flex items-center gap-3">
              <p className="text-sm">
                Excel laatst opgehaald op: {acquisition?.lastUpdatedExcel}
              </p>
              <ButtonNew
                variant="secondaryNeutral"
                icon={<Repeat />}
                label="Refresh financials"
                onClick={fetchAcquisition}
              />
              <a
                href={acquisition?.sharePointSpreadsheetUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ButtonNew
                  variant="primaryMoss"
                  icon={<ArrowUpRight />}
                  label="Open Excel"
                />
              </a>
            </div>
          </header>
          <section className="grid grid-cols-1 gap-8 md:grid-cols-3">
            <div className="flex flex-col gap-2 rounded-md bg-brick-primary/10">
              <header className="flex items-center justify-center gap-1 rounded-md bg-brick-primary/10 py-2 text-sm font-medium uppercase tracking-wider text-brick-primary">
                <p>Aanbod</p>
              </header>
              <div className="grid grid-cols-3 divide-x divide-neutral-primary/10">
                <div className="col-span-full divide-y divide-neutral-primary/10 [&>*>*:first-child]:justify-between [&>*>*]:flex [&>*>*]:items-center [&>*>*]:justify-end [&>*>*]:px-2 [&>*>*]:py-1.5 *:grid *:grid-cols-3 *:divide-x *:divide-neutral-primary/10">
                  <div className="grid-cols-2!">
                    <p>
                      Vraagprijs (excl. kosten)
                      <CellTooltip content="Excel cell (B9)" />
                    </p>
                    <p className="font-medium">
                      {applyMask(acquisition?.grossAskingPrice, "valuta")}
                    </p>
                  </div>
                  <div className="grid-cols-2!">
                    <p>
                      Vraagprijs (incl. kosten)
                      <CellTooltip content="Excel cell (C13)" />
                    </p>
                    <p className="font-medium">
                      {applyMask(acquisition?.netAskingPrice, "valuta")}
                    </p>
                  </div>
                  <div className="grid-cols-2!">
                    <p>
                      Transactiekosten (excl. btw)
                      <CellTooltip content="Excel cell (B31)" />
                    </p>
                    <p className="font-medium">
                      {applyMask(
                        acquisition?.transactionCostsGross,
                        "percentage_comma",
                      )}
                    </p>
                  </div>
                  <div className="grid-cols-2!">
                    <p>
                      Makelaarskosten (excl. btw)
                      <CellTooltip content="Excel cell (B31)" />
                    </p>
                    <p className="font-medium">
                      {applyMask(
                        acquisition?.brokerCostsGross,
                        "percentage_comma",
                      )}
                    </p>
                  </div>
                  <div className="grid-cols-2!">
                    <p>
                      Renovatiekosten <CellTooltip content="Excel cell (B32)" />
                    </p>
                    <p className="font-medium">
                      {applyMask(acquisition?.renovationBudget, "valuta")}
                    </p>
                  </div>
                  <div className="grid-cols-2!">
                    <p>
                      Gem. prijs/res. unit (incl. kosten en renovatie)
                      <CellTooltip content="Excel cell (D40)" />
                    </p>
                    <p className="font-medium">
                      {applyMask(
                        acquisition?.avgPriceResidentialUnit,
                        "valuta",
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2 rounded-md bg-brick-primary/10">
              <header className="flex items-center justify-center gap-1 rounded-md bg-brick-primary/10 py-2 text-sm font-medium uppercase tracking-wider text-brick-primary">
                <p>Samenstelling gebouw</p>
              </header>
              <div className="grid grid-cols-1 divide-x divide-neutral-primary/10">
                <div className="divide-y divide-neutral-primary/10 [&>*>*:first-child]:justify-between [&>*>*]:flex [&>*>*]:items-center [&>*>*]:justify-end [&>*>*]:px-2 [&>*>*]:py-1.5 *:grid *:grid-cols-2 *:divide-x *:divide-neutral-primary/10">
                  <div>
                    <p>
                      Residentiële units
                      <CellTooltip content="Excel cell (E9)" />
                    </p>
                    <p className="font-medium">
                      {applyMask(acquisition?.totalResidentialUnits, "decimal")}
                    </p>
                  </div>
                  <div>
                    <p>
                      Niet-residentiële units
                      <CellTooltip content="Excel cell (E10)" />
                    </p>
                    <p className="font-medium">
                      {applyMask(
                        acquisition?.totalNonResidentialUnits,
                        "decimal",
                      )}
                    </p>
                  </div>
                  <div>
                    <p>
                      Overdekte parking
                      <CellTooltip content="Excel cell (E11)" />
                    </p>
                    <p className="font-medium">
                      {applyMask(
                        acquisition?.totalParkingsWithCover,
                        "decimal",
                      )}
                    </p>
                  </div>
                  <div>
                    <p>
                      Niet-overdekte parking
                      <CellTooltip content="Excel cell (E12)" />
                    </p>
                    <p className="font-medium">
                      {applyMask(acquisition?.totalParkingsNoCover, "decimal")}
                    </p>
                  </div>
                  <div>
                    <p>
                      Bergingen
                      <CellTooltip content="Excel cell (E13)" />
                    </p>
                    <p className="font-medium">
                      {applyMask(acquisition?.totalStorages, "decimal")}
                    </p>
                  </div>
                  <div>
                    <p>
                      Fietsenstallingen
                      <CellTooltip content="Excel cell (E14)" />
                    </p>
                    <p className="font-medium">
                      {applyMask(acquisition?.bicycleStorages, "decimal")}
                    </p>
                  </div>
                  <div>
                    <p>
                      Opp. residentieel
                      <CellTooltip content="Excel cell (E17)" />
                    </p>
                    <p className="font-medium">
                      {applyMask(acquisition?.surfaceResidential, "surface")}
                    </p>
                  </div>
                  <div>
                    <p>
                      Opp. niet-residentieel
                      <CellTooltip content="Excel cell (E16)" />
                    </p>
                    <p className="font-medium">
                      {applyMask(acquisition?.surfaceNonResidential, "surface")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-2 rounded-md bg-brick-primary/10">
              <header className="flex items-center justify-center gap-1 rounded-md bg-brick-primary/10 py-2 text-sm font-medium uppercase tracking-wider text-brick-primary">
                <p>Berekening Yally</p>
              </header>
              <div className="col-span-3 divide-y divide-neutral-primary/10 [&>*>*:first-child]:justify-between [&>*>*]:flex [&>*>*]:items-center [&>*>*]:justify-end [&>*>*]:px-2 [&>*>*]:py-1.5 *:grid *:grid-cols-3 *:divide-x *:divide-neutral-primary/10">
                <div className="grid-cols-2!">
                  <p>Waarde parkings</p>
                  <p className="font-medium">
                    {" "}
                    {applyMask(acquisition?.valueParkings, "valuta")}
                  </p>
                </div>
                <div className="grid-cols-2!">
                  <p>Waarde bergingen</p>
                  <p className="font-medium">
                    {" "}
                    {applyMask(acquisition?.valueStorages, "valuta")}
                  </p>
                </div>
                <div className="grid-cols-2!">
                  <p>Waarde fietsenstallingen</p>
                  <p className="font-medium">
                    {" "}
                    {applyMask(acquisition?.valueBicycleStorages, "valuta")}
                  </p>
                </div>
                <div className="grid-cols-2!">
                  <p>Waarde niet-residentieel</p>
                  <p className="font-medium">
                    {" "}
                    {applyMask(acquisition?.valueNonResidential, "valuta")}
                  </p>
                </div>
                <div className="grid-cols-2!">
                  <p>
                    Biedprijs residentieel
                    <CellTooltip content="Excel cell (G13)" />
                  </p>
                  <p className="font-medium">
                    {applyMask(acquisition?.valueResidential, "valuta")}
                  </p>
                </div>
                <div className="grid-cols-2!">
                  <p className="font-medium">
                    Totaal (= Biedprijs (excl. kosten, actual))
                  </p>
                  <p className="font-medium">
                    {" "}
                    {applyMask(acquisition?.totalBid, "valuta")}
                  </p>
                </div>
                <div className="grid-cols-2!">
                  <p>
                    <span>
                      Biedprijs/m<sup>2</sup> residentieel (incl. kosten en
                      renovatiekosten)
                    </span>
                    <CellTooltip content="Excel cell (D42)" />
                  </p>
                  <p className="font-medium">
                    {applyMask(acquisition?.bidPriceSqmResidential, "valuta")}
                  </p>
                </div>
                <div className="grid-cols-2!">
                  <p>
                    <span>
                      Vraagprijs/m<sup>2</sup> residentieel
                    </span>
                  </p>
                  <p className="font-medium">
                    {applyMask(
                      acquisition?.askingPriceSqmResidential,
                      "valuta",
                    )}
                  </p>
                </div>
                <div className="grid-cols-2!">
                  <p>
                    Fiscale korting meerwaarde vastgoed
                    <CellTooltip content="Excel cell (G19)" />
                  </p>
                  <p className="font-medium">
                    {applyMask(acquisition?.fiscalDiscountRealEstate, "valuta")}
                  </p>
                </div>
                <div className="grid-cols-2!">
                  <p>
                    Fiscale korting liquidatie vennootschap
                    <CellTooltip content="Excel cell (G20)" />
                  </p>
                  <p className="font-medium">
                    {applyMask(
                      acquisition?.fiscalDiscountCompanyShares,
                      "valuta",
                    )}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-2 rounded-md bg-brick-primary/10">
              <header className="flex items-center justify-center gap-1 rounded-md bg-brick-primary/10 py-2 text-sm font-medium uppercase tracking-wider text-brick-primary">
                <p>Assumpties bod</p>
              </header>
              <div className="grid grid-cols-3 divide-x divide-neutral-primary/10">
                <div className="col-span-full divide-y divide-neutral-primary/10 [&>*>*:first-child]:justify-between [&>*>*]:flex [&>*>*]:items-center [&>*>*]:justify-end [&>*>*]:px-2 [&>*>*]:py-1.5 *:grid *:grid-cols-3 *:divide-x *:divide-neutral-primary/10">
                  <div>
                    <span className="col-start-2 justify-self-end">
                      voor transactiekosten
                    </span>
                    <span>na transactiekosten</span>
                  </div>
                  <div>
                    <p>
                      Gesch. yield kantoren/magazijn
                      <CellTooltip content="Excel cell (B15)" />
                    </p>
                    <p className="font-medium">
                      {applyMask(
                        acquisition?.estimatedYieldOfficeStorage,
                        "percentage_comma",
                      )}
                    </p>
                    <p className="font-medium">
                      {applyMask(
                        acquisition?.estimatedYieldOfficeStorageNet,
                        "percentage_comma",
                      )}
                    </p>
                  </div>
                  <div>
                    <p>
                      Gesch. yield retail
                      <CellTooltip content="Excel cell (B16)" />
                    </p>
                    <p className="font-medium">
                      {applyMask(
                        acquisition?.estimatedYieldRetail,
                        "percentage_comma",
                      )}
                    </p>
                    <p className="font-medium">
                      {applyMask(
                        acquisition?.estimatedYieldRetailNet,
                        "percentage_comma",
                      )}
                    </p>
                  </div>
                  <div>
                    <p>
                      Prijs PP overdekt
                      <CellTooltip content="Excel cell (B17)" />
                    </p>
                    <p className="font-medium">
                      {applyMask(
                        acquisition?.priceParkingPlacesCovered,
                        "valuta",
                      )}
                    </p>
                    <p className="font-medium">
                      {applyMask(
                        acquisition?.priceParkingPlacesCoveredNet,
                        "valuta",
                      )}
                    </p>
                  </div>
                  <div>
                    <p>
                      Prijs PP niet overdekt
                      <CellTooltip content="Excel cell (B18)" />
                    </p>
                    <p className="font-medium">
                      {applyMask(
                        acquisition?.priceParkingPlacesNotCovered,
                        "valuta",
                      )}
                    </p>
                    <p className="font-medium">
                      {applyMask(
                        acquisition?.priceParkingPlacesNotCoveredNet,
                        "valuta",
                      )}
                    </p>
                  </div>
                  <div>
                    <p>
                      Prijs berging
                      <CellTooltip content="Excel cell (B19)" />
                    </p>
                    <p className="font-medium">
                      {applyMask(acquisition?.priceStorage, "valuta")}
                    </p>
                    <p className="font-medium">
                      {applyMask(acquisition?.priceStorageNet, "valuta")}
                    </p>
                  </div>
                  <div>
                    <p>
                      Prijs fietsenstalling
                      <CellTooltip content="Excel cell (B20)" />
                    </p>
                    <p className="font-medium">
                      {applyMask(acquisition?.priceBicycleStorage, "valuta")}
                    </p>
                    <p className="font-medium">
                      {applyMask(acquisition?.priceBicycleStorageNet, "valuta")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>
          <section className="flex flex-col gap-2">
            <header className="flex items-center justify-center gap-1 rounded-md bg-brick-primary/10 py-2 text-sm font-medium uppercase tracking-wider text-brick-primary">
              <p>Bod Yally</p>
            </header>
            <div className="flex flex-col divide-y divide-neutral-primary/10">
              <div className="grid grid-cols-8 divide-x divide-neutral-primary/10 font-medium *:px-2 *:py-1.5">
                <p className="col-start-2">€ Totaal</p>
                <p>Nettorendement</p>
                <p>
                  €/m<sup>2</sup> res (= biedprijs res. / m<sup>2 res.</sup>)
                </p>
                <p>Sensiviteit</p>
                <p>
                  HP/m<sup>2</sup>/m res
                </p>
                <p>Bruto huur</p>
                <p>Netto huur</p>
              </div>
              <div className="grid grid-cols-8 divide-x divide-neutral-primary/10 *:px-2 *:py-1.5">
                <p className="font-medium">Biedprijs (excl. kosten, actual)</p>
                <p>{applyMask(acquisition?.bidPriceActual, "valuta")}</p>
                <p>
                  {applyMask(
                    acquisition?.bidPriceActualNetYield,
                    "percentage_comma",
                  )}
                </p>
                <p>{applyMask(acquisition?.bidPriceActualPerSqm, "valuta")}</p>
                <p></p>
                <p>
                  {applyMask(acquisition?.rentActualPerSqm, "valuta_specific")}
                </p>
                <p>
                  {applyMask(acquisition?.potentialGrossIncomeActual, "valuta")}
                </p>
                <p>
                  {applyMask(acquisition?.netOperatingIncomeActual, "valuta")}
                </p>
              </div>
              <div className="grid grid-cols-8 divide-x divide-neutral-primary/10 *:px-2 *:py-1.5">
                <p className="font-medium">
                  Biedprijs (excl. kosten, worst case)
                </p>
                <p>{applyMask(acquisition?.bidPriceWorstCase, "valuta")}</p>
                <p>
                  {applyMask(
                    acquisition?.bidPriceActualNetYield,
                    "percentage_comma",
                  )}
                </p>
                <p>
                  {applyMask(acquisition?.bidPriceWorstCasePerSqm, "valuta")}
                </p>
                <p></p>
                <p>
                  {applyMask(
                    acquisition?.rentWorstCasePerSqm,
                    "valuta_specific",
                  )}
                </p>
                <p>
                  {applyMask(
                    acquisition?.potentialGrossIncomeWorstCase,
                    "valuta",
                  )}
                </p>
                <p>
                  {applyMask(
                    acquisition?.netOperatingIncomeWorstCase,
                    "valuta",
                  )}
                </p>
              </div>
              <div className="grid grid-cols-8 divide-x divide-neutral-primary/10 *:px-2 *:py-1.5">
                <p className="font-medium">
                  Biedprijs (excl. kosten, best case)
                </p>
                <p>{applyMask(acquisition?.bidPriceBestCase, "valuta")}</p>
                <p>
                  {applyMask(
                    acquisition?.bidPriceBestCaseNetYield,
                    "percentage_comma",
                  )}
                </p>
                <p>
                  {applyMask(acquisition?.bidPriceBestCasePerSqm, "valuta")}
                </p>
                <p></p>
                <p>
                  {applyMask(
                    acquisition?.rentBestCasePerSqm,
                    "valuta_specific",
                  )}
                </p>
                <p>
                  {applyMask(
                    acquisition?.potentialGrossIncomeBestCase,
                    "valuta",
                  )}
                </p>
                <p>
                  {applyMask(acquisition?.netOperatingIncomeBestCase, "valuta")}
                </p>
              </div>
              <div className="grid grid-cols-8 divide-x divide-neutral-primary/10 *:px-2 *:py-1.5">
                <p className="font-medium">Transactiekosten</p>
                <p>{applyMask(acquisition?.transactionCosts, "valuta")}</p>
                <p>
                  {applyMask(
                    acquisition?.transactionCostsNetYield,
                    "percentage_comma",
                  )}
                </p>
                <p>Gemiddelde m² / res. unit</p>
                <p></p>
                <p></p>
                <p></p>
                <p></p>
              </div>
              <div className="grid grid-cols-8 divide-x divide-neutral-primary/10 *:px-2 *:py-1.5">
                <p className="font-medium">Renovatiebudget</p>
                <p>{applyMask(acquisition?.renovationBudget, "valuta")}</p>
                <p>
                  {applyMask(
                    acquisition?.renovationBudgetNetYield,
                    "percentage_comma",
                  )}
                </p>
                <p>
                  {applyMask(
                    acquisition?.renovationBudgetPerSqm,
                    "surface_specific",
                  )}
                </p>
                <p></p>
                <p></p>
                <p></p>
                <p></p>
              </div>
              <div className="grid grid-cols-8 divide-x divide-neutral-primary/10 *:px-2 *:py-1.5">
                <p className="font-medium">Totale cash out</p>
                <p>{applyMask(acquisition?.totalCashOut, "valuta")}</p>
                <p>
                  {applyMask(
                    acquisition?.totalCashOutNetYield,
                    "percentage_comma",
                  )}
                </p>
                <p></p>
                <p></p>
                <p></p>
                <p></p>
                <p></p>
              </div>
              <div className="grid grid-cols-8 divide-x divide-neutral-primary/10 *:px-2 *:py-1.5">
                <p className="font-medium">Totale korting (Aandelen + vastgoed)</p>
                <p>
                  {applyMask(
                    (Number(acquisition?.fiscalDiscountCompanyShares) || 0) + (Number(acquisition?.fiscalDiscountRealEstate) || 0),
                    "valuta",
                  )}
                </p>
                <p></p>
                <p></p>
                <p></p>
                <p></p>
                <p></p>
                <p></p>
              </div>
              <div className="grid grid-cols-8 divide-x divide-neutral-primary/10 *:px-2 *:py-1.5">
                <p className="font-medium">Totale cash out na korting</p>
                <p>{applyMask(acquisition?.totalCashOut, "valuta")}</p>
                <p></p>
                <p></p>
                <p></p>
                <p></p>
                <p></p>
                <p></p>
              </div>
            </div>
          </section>
        </section>
        <section className="col-span-full grid grid-cols-1 gap-8 sm:grid-cols-2">
          <header className="col-span-full flex items-center justify-end gap-3">
            <p className="text-sm">
              Excel laatst binnengetrokken op: {acquisition?.lastUpdatedExcel}
            </p>
            <ButtonNew
              variant="secondaryNeutral"
              icon={<Repeat />}
              label="Refresh financials"
              onClick={fetchAcquisition}
            />
            <a
              href={acquisition?.sharePointSpreadsheetUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ButtonNew
                variant="primaryMoss"
                icon={<ArrowUpRight />}
                label="Open Excel"
              />
            </a>
          </header>
          {/* <div>
            {renderSection({
              title: `Evaluatie vastgoed totaal (${(acquisition?.buildings && acquisition.buildings.length) || 0
                })`,
              icon: <AlertInfo />,
              fieldsConfig: statusFieldsEvalProperty,
              ...sharedAcquisitionProps,
            })}
          </div>
          <div>
            {renderSection({
              title: "Bod Yally",
              icon: <AlertInfo />,
              fieldsConfig: yallyBidFields,
              ...sharedAcquisitionProps,
            })}
          </div> */}
          <div>
            {renderSection({
              title: `Evaluatie huur totaal (${(acquisition?.buildings && acquisition.buildings.length) || 0
                }) — Excel`,
              icon: <Euro />,
              fieldsConfig: statusFieldsEvalRent,
              ...sharedAcquisitionProps,
              showEditToggle: false,
            })}
            <p className="text-xs text-neutral-500 italic mt-1 px-3">
              Deze waarden worden automatisch uit de gekoppelde Excel gehaald
            </p>
          </div>
          <div>
            {renderSection({
              title: "Overige informatie",
              icon: <AlertInfo />,
              fieldsConfig: statusFieldsGeneral,
              ...sharedAcquisitionProps,
            })}
          </div>
          <div>
            {renderSection({
              title: "Appreciatie van de transactie",
              icon: <AlertInfo />,
              fieldsConfig: appreciationFieldsGeneral,
              ...sharedAcquisitionProps,
            })}
          </div>
          <div>
            <AcquisitionEmailsList acquisitionId={parseInt(acquisitionId as string)} />
          </div>
          <div className="col-span-full">
            <AcquisitionDocumentsList
              sharePointFolderId={acquisition?.sharePointFolderId || ""}
              sharePointFolderUrl={acquisition?.sharePointFolderUrl || ""}
              refetchDocuments={fetchAcquisition}
              acquisition={acquisition}
            />
          </div>
        </section>
      </section>

      {/* Complete Acquisition Modal */}
      <AddAcquisition
        isOpen={isCompleteModalOpen}
        onOpenChange={setIsCompleteModalOpen}
        onClose={() => {
          setIsCompleteModalOpen(false);
          fetchAcquisition();
        }}
        initialPhase={acquisition?.acquisitionPhase || AcquisitionPhase.INBOX}
        existingAcquisition={acquisition || null}
      />

      {/* Create Contact Modal - rendered at parent level for stability */}
      <CreateContactModal
        isOpen={createContactModalOpen}
        onClose={() => {
          setCreateContactModalOpen(false);
          setCreateContactInitialName('');
          setCreateContactTargetField(null);
        }}
        onContactCreated={async (contact: ResourceValue) => {
          if (createContactTargetField === 'intermediary') {
            setEditedAcquisition((prev: any) => ({
              ...prev,
              intermediary: contact,
            }));
            // Auto-save to backend
            await saveContactToAcquisition('intermediary', contact);
          } else if (createContactTargetField === 'owner') {
            setEditedAcquisition((prev: any) => ({
              ...prev,
              owner: contact,
            }));
            // Auto-save to backend
            await saveContactToAcquisition('owner', contact);
          }
          setCreateContactModalOpen(false);
          setCreateContactInitialName('');
          setCreateContactTargetField(null);
        }}
        initialName={createContactInitialName}
      />
    </div>
  );
};

export default AcquisitionDetails;
